import { oc } from "@orpc/contract";
import { z } from "zod";
import { FEEDBACK_KINDS, FEEDBACK_STATUSES } from "@erp/shared-types";
import {
  CommentResponseSchema,
  CommentVisibilitySchema,
  ListCommentsResponseSchema,
  ListMentionableUsersResponseSchema,
} from "../comments.contract";

/**
 * In-app feedback: filed from the "Send feedback" dialog (or by hand from
 * the dashboard), stored in `core_feedback`, triaged on /feedback-triage.
 *
 * Images travel as presigned PUTs to blob storage — `createUploadUrl` mints a
 * pending `core_files` row, the browser uploads straight to it, and `submit`
 * claims the file ids. Bytes never pass through a handler.
 */

export const FeedbackStatusSchema = z.enum(FEEDBACK_STATUSES);
export const FeedbackKindSchema = z.enum(FEEDBACK_KINDS);

/** Only image types the dashboard can preview inline. */
export const FEEDBACK_IMAGE_CONTENT_TYPE_RE = /^image\/(png|jpeg|gif|webp)$/;

export const FEEDBACK_MAX_ATTACHMENTS = 20;

export const FeedbackContextSchema = z.object({
  userAgent: z.string().max(1024).optional(),
  screenWidth: z.number().int().nonnegative().optional(),
  screenHeight: z.number().int().nonnegative().optional(),
  viewportWidth: z.number().int().nonnegative().optional(),
  viewportHeight: z.number().int().nonnegative().optional(),
  timestamp: z.iso.datetime().optional(),
  locale: z.string().max(32).optional(),
});

export const FeedbackAttachmentKindSchema = z.enum([
  "auto_screenshot",
  "upload",
]);

export const FeedbackScreenshotSchema = z.object({
  fileId: z.uuid(),
  fileName: z.string(),
  signedUrl: z.string(),
  kind: FeedbackAttachmentKindSchema,
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  fileSizeBytes: z.number().nullable(),
});

export const FeedbackUserRefSchema = z.object({
  id: z.uuid(),
  name: z.string(),
});

export const FeedbackListItemSchema = z.object({
  id: z.uuid(),
  /**
   * Per-organization ticket number, shown as `FB-<n>`. Null only on rows
   * filed before numbering existed and not yet backfilled.
   */
  number: z.number().int().nullable(),
  organizationId: z.uuid(),
  organizationName: z.string(),
  /** Who experienced the bug. */
  reporter: FeedbackUserRefSchema.nullable(),
  /** Who filed it; differs from `reporter` when filed on someone's behalf. */
  submittedBy: FeedbackUserRefSchema.nullable(),
  /** Who is working on it; null until a triager assigns someone. */
  owner: FeedbackUserRefSchema.nullable(),
  status: FeedbackStatusSchema,
  kind: FeedbackKindSchema,
  messagePreview: z.string(),
  pageUrl: z.string().nullable(),
  pageTitle: z.string().nullable(),
  screenshotCount: z.number().int(),
  commentCount: z.number().int(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  resolvedAt: z.iso.datetime().nullable(),
  /** When the team first answered (shared comment or triage update). */
  firstRespondedAt: z.iso.datetime().nullable(),
  duplicateOfId: z.uuid().nullable(),
});

export const FeedbackDetailSchema = FeedbackListItemSchema.extend({
  message: z.string(),
  context: FeedbackContextSchema.nullable(),
  resolutionNotes: z.string().nullable(),
  resolvedBy: FeedbackUserRefSchema.nullable(),
  screenshots: z.array(FeedbackScreenshotSchema),
  duplicateOf: z
    .object({
      id: z.uuid(),
      number: z.number().int().nullable(),
      messagePreview: z.string(),
    })
    .nullable(),
});

/**
 * `own` = what the caller reported, filed, or owns. `org` = the caller's
 * organization; only triagers (Feedback:Update) get it, everyone else is
 * coerced to `own`. `all` = every organization; only Gently staff get it,
 * everyone else is silently coerced back to `org` so a shared dashboard URL
 * degrades instead of erroring.
 */
export const FeedbackScopeSchema = z.enum(["own", "org", "all"]);

/** Which timestamp a date window applies to. */
export const FeedbackDateBasisSchema = z.enum(["createdAt", "resolvedAt"]);

export const FeedbackHistoryFieldSchema = z.enum([
  "status",
  "kind",
  "resolution_notes",
  "duplicate_of_id",
  "reporter_user_id",
  "owner_user_id",
]);

export const FeedbackHistoryEntrySchema = z.object({
  id: z.string(),
  timestamp: z.iso.datetime(),
  actor: FeedbackUserRefSchema.nullable(),
  sourceType: z.string().nullable(),
  kind: z.enum(["created", "field_changed"]),
  field: FeedbackHistoryFieldSchema.optional(),
  previousValue: z.string().nullable(),
  value: z.string().nullable(),
});

const FeedbackSortBySchema = z.enum([
  "number",
  "createdAt",
  "updatedAt",
  "status",
]);

/** The filters `list` and `summary` share, so the stats describe the table. */
const FeedbackFilterFieldsSchema = z.object({
  scope: FeedbackScopeSchema.default("org"),
  /** Staff only: narrow `scope: "all"` to one organization. */
  organizationId: z.uuid().optional(),
  status: z.array(FeedbackStatusSchema).optional(),
  kind: z.array(FeedbackKindSchema).optional(),
  /** Only feedback reported by any of these users. */
  reporterUserIds: z.array(z.uuid()).max(50).optional(),
  /** Only feedback reported by members of any of these user groups (teams). */
  userGroupIds: z.array(z.uuid()).max(50).optional(),
  /** Only feedback owned by any of these users. */
  ownerUserIds: z.array(z.uuid()).max(50).optional(),
  search: z.string().max(200).optional(),
  /** Inclusive calendar-day window (`YYYY-MM-DD`, UTC days) on `dateBasis`. */
  dateBasis: FeedbackDateBasisSchema.default("createdAt"),
  dateFrom: z.iso.date().optional(),
  dateTo: z.iso.date().optional(),
});

export const ListFeedbackItemsInputSchema = FeedbackFilterFieldsSchema.extend({
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(25),
  sortBy: FeedbackSortBySchema.default("createdAt"),
  sortDirection: z.enum(["asc", "desc"]).default("desc"),
});

export const FeedbackSummaryInputSchema = FeedbackFilterFieldsSchema.extend({
  /**
   * Narrow the `stats` block (new / in review / backlog / open counts and the response
   * and resolution times) to one type; the dashboard asks for bugs by
   * default. Omitted = every type in the filter. The totals and breakdowns
   * are not affected.
   */
  statsKind: FeedbackKindSchema.optional(),
});

const BreakdownCountsSchema = z.object({
  total: z.number().int(),
  /** new + in_triage + backlog */
  open: z.number().int(),
  /** done + canceled + duplicate */
  resolved: z.number().int(),
});

const StatusCountsSchema = z.record(FeedbackStatusSchema, z.number().int());
const KindCountsSchema = z.record(FeedbackKindSchema, z.number().int());

/** Hours from creation to an event, over the items that reached it. */
const ElapsedStatsSchema = z.object({
  count: z.number().int(),
  averageHours: z.number().nullable(),
  medianHours: z.number().nullable(),
});

export const FeedbackSummarySchema = z.object({
  total: z.number().int(),
  open: z.number().int(),
  resolved: z.number().int(),
  byStatus: StatusCountsSchema,
  byKind: KindCountsSchema,
  /**
   * The dashboard's stat tiles, over the current filter narrowed to `kind`
   * when one was asked for (null = every type).
   */
  stats: z.object({
    kind: FeedbackKindSchema.nullable(),
    byStatus: StatusCountsSchema,
    /** new + in_triage + backlog */
    open: z.number().int(),
    /**
     * Created → first shared comment by the team or first triage update.
     * Both elapsed stats only count items created on or after their
     * organization's `averageTimesStartDate` (see `FeedbackStatsSettings`).
     */
    firstResponse: ElapsedStatsSchema,
    /** Created → resolved. */
    resolution: ElapsedStatsSchema,
    /**
     * That start date (YYYY-MM-DD) when the stats cover one organization;
     * null across organizations, where each uses its own.
     */
    averageTimesStartDate: z.iso.date().nullable(),
  }),
  byReporter: z.array(
    BreakdownCountsSchema.extend({ userId: z.uuid(), name: z.string() }),
  ),
  /** Reporters' user groups (teams); a reporter in two groups counts in both. */
  byGroup: z.array(
    BreakdownCountsSchema.extend({ groupId: z.uuid(), name: z.string() }),
  ),
  /** Present only for staff `scope: "all"`; also feeds the org filter. */
  byOrganization: z
    .array(
      z.object({
        organizationId: z.uuid(),
        organizationName: z.string(),
        total: z.number().int(),
        byStatus: StatusCountsSchema,
      }),
    )
    .optional(),
});

export const SubmitFeedbackInputSchema = z.object({
  message: z.string().min(1).max(5000),
  kind: FeedbackKindSchema,
  pageUrl: z.string().max(2048).optional(),
  pageTitle: z.string().max(512).optional(),
  context: FeedbackContextSchema.optional(),
  attachments: z
    .array(
      z.object({
        fileId: z.uuid(),
        kind: FeedbackAttachmentKindSchema,
      }),
    )
    .max(FEEDBACK_MAX_ATTACHMENTS)
    .default([]),
  /**
   * File on another user's behalf. Requires Feedback:Update (or Gently
   * staff) and the target must be an active user of the caller's org.
   * Omitted = the caller is the reporter.
   */
  reporterUserId: z.uuid().optional(),
  /**
   * Assign the new ticket to this person and notify them. Any signed-in
   * user may set it; the target must be an active Gently user
   * (@gently-ai.com or @gentlyhq.com) of the caller's organization.
   * Omitted = unassigned.
   */
  directedToUserId: z.uuid().optional(),
});

/**
 * A feedback the caller points at: its uuid, or its ticket number in any of
 * the shapes people type it (`12`, `#12`, `FB-12`). Resolved server-side
 * within the report's organization.
 */
export const FeedbackRefSchema = z.string().trim().min(1).max(64);

export const UpdateFeedbackInputSchema = z
  .object({
    id: z.uuid(),
    status: FeedbackStatusSchema.optional(),
    kind: FeedbackKindSchema.optional(),
    resolutionNotes: z.string().max(10_000).nullable().optional(),
    duplicateOfId: FeedbackRefSchema.nullable().optional(),
    /**
     * Reassign who the feedback is for (e.g. an admin filed it under their
     * own name). Requires Feedback:Update; the target must be an active user
     * of the report's organization.
     */
    reporterUserId: z.uuid().optional(),
    /**
     * Assign (uuid) or clear (null) who is working on it. Requires
     * Feedback:Update; the target must be an active user of the report's
     * organization. A new owner is notified.
     */
    ownerUserId: z.uuid().nullable().optional(),
  })
  .refine(
    (input) =>
      input.status !== undefined ||
      input.kind !== undefined ||
      input.resolutionNotes !== undefined ||
      input.duplicateOfId !== undefined ||
      input.reporterUserId !== undefined ||
      input.ownerUserId !== undefined,
    { message: "Provide at least one field to update" },
  );

/**
 * Options for the dashboard's reporter and team filters. Scoped by the
 * caller's Feedback grant rather than User/UserGroup grants, so a triager
 * who cannot administer users still gets a working filter bar.
 */
export const FeedbackFilterOptionsInputSchema = z.object({
  /** Staff only: another organization's options (ignored otherwise). */
  organizationId: z.uuid().optional(),
});

export const FeedbackFilterOptionsSchema = z.object({
  reporters: z.array(FeedbackUserRefSchema),
  groups: z.array(z.object({ id: z.uuid(), name: z.string() })),
});

/**
 * One organization's feedback notification settings, edited from the
 * feedback page by Feedback:Update holders. In-app notifications are always
 * on; `emailEnabled` sends email as well — for new feedback, owner
 * assignment and @mentions — and overrides each recipient's personal
 * in-app-only preference. The recipient lists only apply to new feedback:
 * configured people plus every member of the configured teams; both empty
 * means everyone who can read feedback, as before.
 */
export const FeedbackNotificationSettingsSchema = z.object({
  emailEnabled: z.boolean(),
  recipientUserGroupIds: z.array(z.uuid()).max(50),
  recipientUserIds: z.array(z.uuid()).max(200),
});

/**
 * One organization's feedback stats settings, edited from the feedback page
 * by Feedback:Update holders. `averageTimesStartDate` (YYYY-MM-DD, UTC): only
 * feedback created on or after it counts toward the average (and median) time
 * to first response and to resolution, so
 * tickets imported or normalized from before the dashboard do not skew them.
 */
export const FeedbackStatsSettingsSchema = z.object({
  averageTimesStartDate: z.iso.date(),
});

export const feedbackContracts = {
  /** Mint a presigned PUT for one image; returns the pending file id. */
  createUploadUrl: oc
    .input(
      z.object({
        fileName: z.string().min(1).max(255),
        contentType: z.string().regex(FEEDBACK_IMAGE_CONTENT_TYPE_RE),
      }),
    )
    .output(z.object({ fileId: z.uuid(), uploadUrl: z.string() }))
    .route({
      summary: "Get feedback screenshot upload URL",
      description:
        "Creates a pending image file and returns a presigned upload URL for attaching a PNG, JPEG, GIF or WebP screenshot to a feedback report.",
      method: "POST",
    }),

  /** Persist the report, claim the uploaded files, email support, notify. */
  submit: oc
    .input(SubmitFeedbackInputSchema)
    .output(
      z.object({
        id: z.uuid(),
        /** Per-organization ticket number, shown as `FB-<n>`. */
        number: z.number().int().nullable(),
      }),
    )
    .route({
      summary: "Submit feedback or bug report",
      description:
        "Saves a feedback report with message, kind, page and uploaded screenshots, optionally on another user's behalf or directed at a Gently user who becomes the owner; emails support and sends an in-app notification.",
      method: "POST",
    }),

  /** Delete a caller-owned pending file left behind by a failed submit. */
  discardUpload: oc
    .input(z.object({ fileId: z.uuid() }))
    .output(z.object({ success: z.boolean() }))
    .route({
      summary: "Discard unused feedback screenshot",
      description:
        "Deletes a pending screenshot file the user uploaded but never attached to a submitted feedback report.",
      method: "POST",
    }),

  list: oc
    .input(ListFeedbackItemsInputSchema)
    .output(
      z.object({
        items: z.array(FeedbackListItemSchema),
        total: z.number().int(),
        page: z.number().int(),
        pageSize: z.number().int(),
      }),
    )
    .route({
      summary: "List feedback reports",
      description:
        "Returns a paginated, sortable list of feedback reports filtered by status, kind, reporter, owner, team or text search; staff can list across organizations.",
      method: "GET",
    }),

  get: oc
    .input(z.object({ id: z.uuid() }))
    .output(FeedbackDetailSchema)
    .route({
      summary: "Get feedback report",
      description:
        "Returns one feedback report with full message, browser context, screenshots, resolution notes and duplicate link.",
      method: "GET",
    }),

  update: oc
    .input(UpdateFeedbackInputSchema)
    .output(FeedbackDetailSchema)
    .route({
      summary: "Update feedback report status",
      description:
        "Changes a feedback report's status, kind, resolution notes, duplicate-of link, reporter or owner for triage; a newly assigned owner is notified.",
      method: "POST",
    }),

  summary: oc
    .input(FeedbackSummaryInputSchema)
    .output(FeedbackSummarySchema)
    .route({
      summary: "Summarize feedback reports",
      description:
        "Returns feedback counts by status, kind, reporter, team and organization, plus a stats block (new / in review / backlog / open counts and average and median time to first response and to resolution) for one feedback type when statsKind is given, using the same filters as the list.",
      method: "GET",
    }),

  /** Reporter and team choices for the filter bar (Feedback:Read). */
  filterOptions: oc
    .input(FeedbackFilterOptionsInputSchema)
    .output(FeedbackFilterOptionsSchema)
    .route({
      summary: "List feedback filter options",
      description:
        "Returns the reporters and teams available as filters on the feedback triage dashboard for an organization.",
      method: "GET",
    }),

  /** The caller's organization's notification settings (Feedback:Read). */
  getNotificationSettings: oc
    .input(z.object({}))
    .output(FeedbackNotificationSettingsSchema)
    .route({
      summary: "Get feedback notification settings",
      description:
        "Returns the caller's organization's feedback notification settings: whether email is sent as well as in-app notifications, and which teams and people are told about new feedback.",
      method: "GET",
    }),

  /**
   * Replace the caller's organization's notification settings
   * (Feedback:Update). Every id must be a team / active user of that org.
   */
  updateNotificationSettings: oc
    .input(FeedbackNotificationSettingsSchema)
    .output(FeedbackNotificationSettingsSchema)
    .route({
      summary: "Update feedback notification settings",
      description:
        "Replaces the caller's organization's feedback notification settings; every recipient must be a team or active user of that organization.",
      method: "POST",
    }),

  /** The caller's organization's stats settings (Feedback:Read). */
  getStatsSettings: oc
    .input(z.object({}))
    .output(FeedbackStatsSettingsSchema)
    .route({
      summary: "Get feedback stats settings",
      description:
        "Returns the caller's organization's feedback stats settings: the date from which created feedback counts toward the average time to first response and to resolution.",
      method: "GET",
    }),

  /** Replace the caller's organization's stats settings (Feedback:Update). */
  updateStatsSettings: oc
    .input(FeedbackStatsSettingsSchema)
    .output(FeedbackStatsSettingsSchema)
    .route({
      summary: "Update feedback stats settings",
      description:
        "Sets the date from which created feedback counts toward the caller's organization's average time to first response and to resolution, so older imported tickets are left out.",
      method: "POST",
    }),

  /** Status / resolution / duplicate changes, from the audit change log. */
  getHistory: oc
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ entries: z.array(FeedbackHistoryEntrySchema) }))
    .route({
      summary: "Get feedback report history",
      description:
        "Returns the change history of a feedback report: creation plus status, kind, resolution note, duplicate, reporter and owner changes with who made them.",
      method: "GET",
    }),

  listComments: oc
    .input(
      z.object({
        id: z.uuid(),
        limit: z.number().int().min(1).max(200).default(50),
        offset: z.number().int().min(0).default(0),
      }),
    )
    .output(ListCommentsResponseSchema)
    .route({
      summary: "List feedback report comments",
      description: "Returns the paginated comment thread on a feedback report.",
      method: "GET",
    }),

  addComment: oc
    .input(
      z.object({
        id: z.uuid(),
        body: z.string().min(1).max(5000),
        /** `internal` notes are hidden from the reporter's read-only view. */
        visibility: CommentVisibilitySchema.default("shared"),
      }),
    )
    .output(CommentResponseSchema)
    .route({
      summary: "Comment on a feedback report",
      description:
        "Adds a comment to a feedback report; internal comments are hidden from the reporter.",
      method: "POST",
    }),

  /**
   * People who can be @mentioned on this report: active users of the
   * report's organization with Feedback:Read — the same filter the server
   * applies when it resolves mentions on write (Feedback:Read to call).
   */
  listMentionableUsers: oc
    .input(z.object({ id: z.uuid() }))
    .output(ListMentionableUsersResponseSchema)
    .route({
      summary: "List mentionable users for a feedback report",
      description:
        "Returns the active users of the report's organization who hold Feedback:Read, the people a comment on the report can @mention.",
      method: "GET",
    }),
} as const;

export type FeedbackContext = z.infer<typeof FeedbackContextSchema>;
export type FeedbackAttachmentKind = z.infer<
  typeof FeedbackAttachmentKindSchema
>;
export type FeedbackScreenshot = z.infer<typeof FeedbackScreenshotSchema>;
export type FeedbackUserRef = z.infer<typeof FeedbackUserRefSchema>;
export type FeedbackListItem = z.infer<typeof FeedbackListItemSchema>;
export type FeedbackDetail = z.infer<typeof FeedbackDetailSchema>;
export type FeedbackScope = z.infer<typeof FeedbackScopeSchema>;
export type FeedbackDateBasis = z.infer<typeof FeedbackDateBasisSchema>;
export type FeedbackFilterFields = z.input<typeof FeedbackFilterFieldsSchema>;
export type FeedbackKindValue = z.infer<typeof FeedbackKindSchema>;
export type FeedbackHistoryField = z.infer<typeof FeedbackHistoryFieldSchema>;
export type FeedbackHistoryEntry = z.infer<typeof FeedbackHistoryEntrySchema>;
export type ListFeedbackItemsInput = z.input<
  typeof ListFeedbackItemsInputSchema
>;
export type FeedbackSummaryInput = z.input<typeof FeedbackSummaryInputSchema>;
export type FeedbackSummary = z.infer<typeof FeedbackSummarySchema>;
export type FeedbackFilterOptionsInput = z.input<
  typeof FeedbackFilterOptionsInputSchema
>;
export type FeedbackFilterOptions = z.infer<typeof FeedbackFilterOptionsSchema>;
export type FeedbackStatsSettings = z.infer<typeof FeedbackStatsSettingsSchema>;
export type FeedbackNotificationSettings = z.infer<
  typeof FeedbackNotificationSettingsSchema
>;
export type SubmitFeedbackInput = z.input<typeof SubmitFeedbackInputSchema>;
export type UpdateFeedbackInput = z.input<typeof UpdateFeedbackInputSchema>;
